import React, { useReducer, useState, useMemo, type ChangeEvent, type SubmitEvent } from "react";
import * as zod from "zod";
import isEqual from "lodash/isEqual";
import sortBy from "lodash/sortBy";

import {
  useGetProgramTypesQuery,
  useGetCampaignObjectiveTypesQuery,
  useGetAudienceTypesQuery,
  DEFAULT_PROGRAM_TYPE,
  mapDictionaryItemsToMultiSelectOptions,
} from "hooks/api/dictionary";

import { TextField } from "ui/text-field";
import { FormGroup, Form } from "ui/forms";
import { Typography } from "ui/typography";
import { Button, LoadingButton, useAsyncSubmit } from "ui/button";
import { Switch } from "ui/switch";
import { Select, OptionType } from "ui/select";
import { ConfirmationDialog } from "ui/confirmation-dialog";
import FormErrors from "components/partials/form-errors/form-errors";
import toast from "components/partials/toast/toast";
import { CampaignAttributes, CampaignWriteBody } from "hooks/api/use-campaigns";
import { useDisclosure } from "hooks/use-disclosure";
import { getTouchpoint } from "generated/api/touchpoint";

const touchpointApi = getTouchpoint();

import {
  deeplyTransformEmptyStringToUndefined,
  toUpperCaseSpacesToUnderScores,
  toLowerCaseNoSpaces,
} from "utilities";

import { ZodFormErrors } from "types";
import { Formify } from "types/utility";

type CampaignUpdateBody = CampaignWriteBody & { id: string };

interface CreateCampaignFormProps {
  campaign?: CampaignAttributes;
  onCancel: () => void;
  onSubmit: (attributes: CampaignWriteBody) => Promise<CampaignAttributes>;
  onSuccess: (campaign: CampaignAttributes) => void;
}

interface UpdateCampaignFormProps {
  campaign: CampaignAttributes;
  onCancel: () => void;
  onSubmit: (attributes: CampaignUpdateBody) => Promise<CampaignAttributes>;
  onSuccess: (campaign: CampaignAttributes) => void;
}

type CampaignFormProps = CreateCampaignFormProps | UpdateCampaignFormProps;

function isUpdatingCampaign(props: CampaignFormProps): props is UpdateCampaignFormProps {
  return (props as UpdateCampaignFormProps).campaign !== undefined;
}

export const CampaignForm = (props: CampaignFormProps) => {
  const form = useCampaignFormState(props.campaign);
  const confirmCancelModal = useDisclosure();
  const { submit, submitState } = useAsyncSubmit(
    props.onSubmit as (
      attributes: CampaignWriteBody | CampaignUpdateBody,
    ) => Promise<CampaignAttributes>,
    {
      onSuccess: props.onSuccess,
    },
  );

  const initialTransferStudentStatus = useMemo(
    () => props.campaign?.transferStudentStatusFlag ?? false,
    [props.campaign],
  );

  const [transferStudentStatusFlag, setTransferStudentStatus] = useState(
    initialTransferStudentStatus,
  );

  const initialValues = useMemo(() => initialCampaignFormValues(props.campaign), [props.campaign]);

  const isDirty = useMemo(() => {
    // Sort arrays to ensure order doesn't affect equality check
    const sortedInitial = {
      ...initialValues,
      audiences: sortBy(initialValues.audiences),
    };
    const sortedCurrent = {
      ...form.values,
      audiences: sortBy(form.values.audiences),
    };

    const isTransferStudentStatusDirty =
      form.values.programType === DEFAULT_PROGRAM_TYPE
        ? transferStudentStatusFlag !== initialTransferStudentStatus
        : false;

    return !isEqual(sortedInitial, sortedCurrent) || isTransferStudentStatusDirty;
  }, [form.values, initialValues, transferStudentStatusFlag, initialTransferStudentStatus]);

  const { data: audienceDictionaryItems = [] } = useGetAudienceTypesQuery();
  const { data: programTypes = [] } = useGetProgramTypesQuery();

  const { data: campaignObjectiveTypes = [] } = useGetCampaignObjectiveTypesQuery(
    form.values.programType,
  );

  const isEditMode = isUpdatingCampaign(props);

  const audienceOptions = mapDictionaryItemsToMultiSelectOptions(audienceDictionaryItems);

  /**
   * Audiences are held as dictionary names (`TRANSFER_PROSPECTS`) — the shape the API takes, and the
   * shape `ui/select` matches a selected value against. Holding the display label instead left every
   * option unmatched, so the menu checkbox never filled in and a second click on the same option
   * appended a duplicate chip instead of clearing it.
   */
  function handleAudienceMultiSelectChange(selectedOptions: OptionType[]) {
    form.handleComplexChange(
      "audiences",
      selectedOptions.map((option) => String(option.value)),
    );
  }

  async function handleSubmit(e: SubmitEvent<HTMLFormElement>) {
    e.preventDefault();
    try {
      const campaignAttributes = schema.parse(
        deeplyTransformEmptyStringToUndefined({
          ...form.values,
        }),
      );

      form.setErrors({});
      const isAwareToApply = campaignAttributes.programType === DEFAULT_PROGRAM_TYPE;
      const formattedCampaign = {
        ...campaignAttributes,
        campaignObjective:
          campaignAttributes.campaignObjective &&
          (toUpperCaseSpacesToUnderScores(campaignAttributes.campaignObjective) as string),
        audiences: form.values.audiences
          .map((audienceChoice) => {
            return toUpperCaseSpacesToUnderScores(audienceChoice);
          })
          .filter((str) => str !== null) as string[],
        programType: campaignAttributes.programType,
        lastModifiedDate: "",
        cdnSubdirectoryName: campaignAttributes.cdnSubdirectoryName
          ? toLowerCaseNoSpaces(campaignAttributes.cdnSubdirectoryName)
          : "",
        transferStudentStatusFlag: isAwareToApply ? transferStudentStatusFlag : undefined,
      };

      if (isEditMode) {
        const campaignToUpdate: CampaignUpdateBody = {
          ...formattedCampaign,
          id: props.campaign.id,
          lastModifiedDate: props.campaign.lastModifiedDate,
        };
        await submit(campaignToUpdate);
      } else {
        await submit(formattedCampaign);
      }
    } catch (error) {
      if (error instanceof zod.ZodError) {
        form.setErrors(error.flatten().fieldErrors as ZodFormErrors);
      }
    }
  }

  /** Changing Program type must clear the Campaign objective and reset transfer student status. */
  const handleProgramTypeChange = (option: OptionType | null) => {
    const value = (option?.value as string) || "";
    form.handleComplexChange("programType", value);
    form.handleComplexChange("campaignObjective", "");
    setTransferStudentStatus(false);
  };

  const handleCampaignObjectiveChange = (option: OptionType | null) => {
    const value = (option?.value as string) || "";
    form.handleComplexChange("campaignObjective", value);
    // Check if the campaign has a clientId and id (you're updating a campaign rahter than creating a new one)
    if (props.campaign?.id && props.campaign.clientId) {
      // If it does, check if there are any touchpoints associated with the campaign
      touchpointApi
        .getAllTouchpointsForCampaign(
          {
            clientId: props.campaign.clientId,
            campaignId: props.campaign.id,
          },
          { size: 1 },
        )
        .then((response) => {
          if ((response.page?.totalElements ?? 0) > 0) {
            toast.warning({
              data: {
                title: "Campaign objective changed",
                message:
                  "Changing campaign objective may mismatch current creative content types. Please review and update them.",
              },
              autoClose: false,
            });
          }
        });
    }
  };

  const pageTitle = props.campaign ? "Edit Settings" : "Create campaign";

  const isFormComplete =
    form.values.name && form.values.programType && form.values.campaignObjective;

  const handleCancel = () => {
    if (isDirty) {
      confirmCancelModal.onOpen();
    } else {
      props.onCancel();
    }
  };

  return (
    <div className="flex flex-col">
      {Object.entries(form.errors).length > 0 && <FormErrors form={form} />}

      <Typography variant="h2" sx={{ color: "secondary.main" }} className="mb-8 mt-5">
        {pageTitle}
      </Typography>

      <Form onSubmit={handleSubmit}>
        <FormGroup legend="Basic details">
          <Select
            label="Program type"
            selectName="programType"
            ariaLabel="Program type"
            helperText="This can't be edited once campaign is created."
            value={
              mapDictionaryItemsToMultiSelectOptions(programTypes).find(
                (opt) => opt.value === form.values.programType,
              ) || null
            }
            onChange={(option) => handleProgramTypeChange(option)}
            disabled={!!props.campaign}
            options={mapDictionaryItemsToMultiSelectOptions(programTypes)}
            required
          />
          <Switch
            toggleName="Transfer campaign"
            checked={transferStudentStatusFlag}
            onChange={setTransferStudentStatus}
            disabled={form.values.programType !== DEFAULT_PROGRAM_TYPE || !!props.campaign}
          />
          <TextField
            required
            id="campaign-name"
            label="Campaign name"
            value={form.values.name}
            onChange={form.handleChange("name")}
          />
          <TextField
            id="campaign-cdnSubdirectoryName"
            label="Campaign CDN profile"
            helperText="Please enter profile name with no space (e.g. 'campaigndirectoryname') to set up your URL and asset directory."
            value={form.values.cdnSubdirectoryName}
            onChange={form.handleChange("cdnSubdirectoryName")}
          />
        </FormGroup>

        <FormGroup legend="Audience & strategy">
          <Select
            label="Campaign objective"
            selectName="campaignObjective"
            ariaLabel="Campaign objective"
            value={
              mapDictionaryItemsToMultiSelectOptions(campaignObjectiveTypes).find(
                (opt) => opt.value === form.values.campaignObjective,
              ) || null
            }
            onChange={(option) => {
              handleCampaignObjectiveChange(option);
            }}
            options={mapDictionaryItemsToMultiSelectOptions(campaignObjectiveTypes)}
            required
          />
          <Select
            label="Audience"
            selectName="Audience"
            ariaLabel="Audience"
            multiple
            options={audienceOptions}
            value={audienceOptions.filter((option) => form.values.audiences.includes(option.value))}
            onChange={handleAudienceMultiSelectChange}
          />
        </FormGroup>

        <div className="flex items-center gap-2">
          <LoadingButton
            size="large"
            type="submit"
            disabled={!isFormComplete || !isDirty}
            state={submitState}
            loadingMessage={isEditMode ? "Saving changes..." : "Creating campaign..."}
            successMessage={isEditMode ? "Changes saved" : "Campaign created"}
            errorMessage="Error. Try again.">
            {isEditMode ? "Save changes" : "Create campaign"}
          </LoadingButton>
          <Button size="large" variant="text" onClick={handleCancel}>
            Cancel
          </Button>
        </div>
        <ConfirmationDialog
          open={confirmCancelModal.isOpen}
          onClose={confirmCancelModal.onClose}
          headline="Leave without saving?"
          message="All unsaved changes will be lost."
          cancelButtonText="Continue editing"
          confirmButtonText="Leave setup"
          onConfirm={props.onCancel}
          intent="warning"
        />
      </Form>
    </div>
  );
};

const schema = zod.object({
  audiences: zod.string().array().optional(),
  campaignObjective: zod.string(),
  defaultThemeId: zod.string().optional(),
  domainUrl: zod.string().url().optional(),
  name: zod.string(),
  programType: zod.string().default(DEFAULT_PROGRAM_TYPE),
  cdnSubdirectoryName: zod.string().optional(),
});
type CampaignAttributesSchema = zod.infer<typeof schema>;
type CampaignAttributesFormState = Formify<CampaignAttributesSchema>;
type CampaignAttributesFormErrors = Partial<{ [k in keyof CampaignAttributesSchema]: string }>;

function initialCampaignFormValues(campaign?: CampaignAttributes): CampaignAttributesFormState {
  return {
    audiences: campaign?.audiences || [],
    campaignObjective: campaign?.campaignObjective || "",
    defaultThemeId: campaign?.defaultThemeId || "",
    domainUrl: campaign?.domainUrl || "",
    name: campaign?.name || "",
    programType: campaign?.programType || DEFAULT_PROGRAM_TYPE,
    cdnSubdirectoryName: campaign?.cdnSubdirectoryName || "",
  };
}

function useCampaignFormState(campaign?: CampaignAttributes) {
  const [values, dispatch] = useReducer(
    function reducer(
      state: CampaignAttributesFormState,
      action: { field: string; value: string | string[] | undefined },
    ) {
      return {
        ...state,
        [action.field]: action.value || (Array.isArray(action.value) ? [] : ""),
      };
    },
    campaign,
    initialCampaignFormValues,
  );

  function handleChange(field: string) {
    return function (
      event: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
    ) {
      dispatch({ field, value: event.target.value });
    };
  }

  function handleComplexChange(field: string, value: any) {
    dispatch({ field, value });
  }

  const [errors, setErrors] = useState<CampaignAttributesFormErrors>({});

  function handleErrors(errors: ZodFormErrors): void {
    const invalidFields = Object.keys(errors) as Array<keyof CampaignAttributesSchema>;
    setErrors(
      invalidFields.reduce((memo, field) => {
        memo[field] = errors[field].join(", ");
        return memo;
      }, {} as CampaignAttributesFormErrors),
    );
  }

  return { values, handleChange, handleComplexChange, errors, setErrors: handleErrors };
}
